import { useEffect, useRef } from 'react'
import { useEventBus } from 'hooks/useEventBus'
import type { EventBusPayload } from 'services/eventBusService'
import useStore from 'state/knovStore'
import * as cache from 'state/cache'
import api from 'api/api'
import toast from 'utils/toast'
import { ordinalMintPhaseLabel, ordinalMintToastKind } from './ordinalMintPresentation'
import { cancelSystemThreadOpen, openSystemThreadForMintedAnswer } from './ordinalMintNavigation'

/** Post-pay-gate acknowledgement that the mint kicked off and runs in the background. */
export function showOrdinalMintStartedToast(answerId?: string | null) {
    toast.pending('Minting ordinal', {
        kind: ordinalMintToastKind(answerId),
        detail: 'Starting…',
    })
}

export function showOrdinalMintProgressToast(
    answerId: string | null | undefined,
    message?: string | null,
) {
    const kind = ordinalMintToastKind(answerId)
    const detail = ordinalMintPhaseLabel(message)
    const currentNotice = useStore.getState().globalNotice
    if (currentNotice?.kind === kind && currentNotice.variant !== 'pending') return false
    if (currentNotice?.kind === kind) {
        toast.update(kind, 'Minting ordinal', { detail })
    } else {
        toast.pending('Minting ordinal', { kind, detail })
    }
    return true
}

/** One toast slot per mint outcome, shared with the per-media fallback paths so a
 *  double delivery (EventBus + local polling) renders as a single stable pill. */
export function showOrdinalMintSuccessToast(answerId?: string | null) {
    const kind = ordinalMintToastKind(answerId)
    const options = {
        durationMs: 8000,
        actionLabel: 'View',
        cancelLabel: 'Dismiss',
        onAction: () => useStore.getState().actions.modalActions.openOrdinalsModal(),
    }
    if (useStore.getState().globalNotice?.kind === kind) {
        toast.update(kind, 'Ordinal minted', { variant: 'success', detail: '', ...options })
    } else {
        toast.success('Ordinal minted', { kind, ...options })
    }
}

export function showOrdinalMintErrorToast(answerId?: string | null, message?: string | null) {
    const kind = ordinalMintToastKind(answerId)
    const errorMessage =
        typeof message === 'string' && message
            ? message
            : 'Failed to mint ordinal — funds were not spent'
    if (useStore.getState().globalNotice?.kind === kind) {
        toast.update(kind, errorMessage, { variant: 'error', detail: '' })
    } else {
        toast.error(errorMessage, { kind })
    }
}

/**
 * Session-wide listener for `mint_ordinal_status` EventBus events (already scoped to
 * the current user server-side). Mint workflows outlive any single component — stream
 * rows unmount while scrolling and the payload ⋮ menu closes right after the pay gate —
 * so completion UX lives here instead of per-media components: a resolving toast plus a
 * refresh of the affected answer. The in-progress state itself renders from
 * `answer.mint_workflow_id`/`mint_run_id`, persisted server-side at workflow start.
 */
export default function OrdinalMintStatusListener() {
    const { getTopic$, isConnected } = useEventBus()
    const handledCompletions = useRef(new Set<string>())
    const completionRailTimers = useRef(new Map<string, number>())

    useEffect(() => {
        if (!isConnected) return

        const subscription = getTopic$('mint_ordinal_status').subscribe(
            (payload: EventBusPayload) => {
                // Messages can arrive direct or nested one level, same as
                // useTemporalWorkflowStatus handles them.
                const eventPayload = (payload?.payload || payload) as EventBusPayload
                if (!eventPayload) return

                const status = (eventPayload.status || {}) as EventBusPayload
                const completed = eventPayload.completed === true || status.completed === true
                const answerIdValue = status.answer_id || eventPayload.answer_id
                const answerId = answerIdValue ? String(answerIdValue) : null
                const statusMessage =
                    typeof status.message === 'string'
                        ? status.message
                        : typeof eventPayload.message === 'string'
                        ? eventPayload.message
                        : null

                if (!completed) {
                    if (!answerId) return

                    const cachedAnswer = cache.getCachedAnswer(answerId)
                    const progressWorkflowId = eventPayload.workflow_id
                    if (
                        progressWorkflowId &&
                        cachedAnswer?.mint_workflow_id &&
                        cachedAnswer.mint_workflow_id !== progressWorkflowId
                    ) {
                        return
                    }
                    if (
                        cachedAnswer?.mint_recently_completed ||
                        cachedAnswer?.mint_failure_message
                    ) {
                        return
                    }
                    if (!showOrdinalMintProgressToast(answerId, statusMessage)) return
                    if (cachedAnswer) {
                        cache.cacheAnswer({
                            ...cachedAnswer,
                            mint_status_message: statusMessage,
                            mint_failure_message: null,
                            mint_recently_completed: false,
                        })
                    }
                    return
                }

                const completionKey = `${eventPayload.workflow_id}:${eventPayload.run_id}`
                if (handledCompletions.current.has(completionKey)) return
                handledCompletions.current.add(completionKey)

                const isError = status.error === true || eventPayload.error === true
                const completedWorkflowId = eventPayload.workflow_id
                const currentAnswer = answerId ? cache.getCachedAnswer(answerId) : null
                if (
                    completedWorkflowId &&
                    currentAnswer?.mint_workflow_id &&
                    currentAnswer.mint_workflow_id !== completedWorkflowId
                ) {
                    return
                }

                if (isError) {
                    showOrdinalMintErrorToast(answerId, statusMessage)
                    cancelSystemThreadOpen(answerId)
                } else {
                    showOrdinalMintSuccessToast(answerId)
                    // Ordinals-stream mints land on the ordinal's system thread.
                    openSystemThreadForMintedAnswer(answerId)
                }

                // The workflow cleared (or set) minting state server-side; pull the fresh
                // answer so surfaces flip even when its AnswerChannel isn't subscribed.
                if (answerId && cache.getCachedAnswer(answerId)) {
                    api.getAnswer(answerId)
                        .then(answer => {
                            if (!answer) return
                            const currentAnswer = cache.getCachedAnswer(answerId)
                            const currentWorkflowId = currentAnswer?.mint_workflow_id
                            if (
                                completedWorkflowId &&
                                currentWorkflowId &&
                                currentWorkflowId !== completedWorkflowId
                            ) {
                                return
                            }

                            if (
                                completedWorkflowId &&
                                answer.mint_workflow_id &&
                                answer.mint_workflow_id !== completedWorkflowId
                            ) {
                                cache.cacheAnswer(answer)
                                return
                            }
                            cache.cacheAnswer({
                                ...answer,
                                mint_status_message: null,
                                mint_failure_message: isError ? statusMessage : null,
                                mint_recently_completed: !isError,
                            })
                            if (!isError) {
                                const existingTimer = completionRailTimers.current.get(answerId)
                                if (existingTimer) window.clearTimeout(existingTimer)
                                const timer = window.setTimeout(() => {
                                    const completedAnswer = cache.getCachedAnswer(answerId)
                                    if (completedAnswer?.mint_recently_completed) {
                                        cache.cacheAnswer({
                                            ...completedAnswer,
                                            mint_recently_completed: false,
                                        })
                                    }
                                    completionRailTimers.current.delete(answerId)
                                }, 2200)
                                completionRailTimers.current.set(answerId, timer)
                            }
                        })
                        .catch(() => {})
                }
            },
        )

        return () => {
            subscription.unsubscribe()
            completionRailTimers.current.forEach(timer => window.clearTimeout(timer))
            completionRailTimers.current.clear()
        }
    }, [isConnected, getTopic$])

    return null
}
